/**
 * Wally email forms (wally-email, the Klaviyo replacement; app in email/, API at
 * https://go.wally.co.nz). One script for everything the storefront sends it:
 *
 *   1. Signup flyout            POST /forms/subscribe          (snippets/wally-email-flyout.liquid)
 *   2. "Notify me" requests     POST /forms/stock-request      back in stock (snippets/wally-bis-note.liquid)
 *                                                              and pre-order arrival (snippets/wally-preorder-note.liquid)
 *   3. Product views            POST /t                        only for people we know: arrived from one of our
 *                                                              emails (the _wid link parameter), or subscribed
 *                                                              through one of these forms (the reply carries the
 *                                                              same id), so Price Drop knows who looked at what.
 *                                                              Before that, the last 20 product ids viewed stay on
 *                                                              this device only (30 days) and go once they're known,
 *                                                              so their first emails can show what they looked at.
 *
 * The notify forms sit inside the buy-buttons block and are swapped wholesale on variant
 * change by wally-preorder.js, so every handler here is delegated.
 * Loaded on every page by snippets/wally-tracking.liquid.
 */
(function () {
  'use strict';

  var API = 'https://go.wally.co.nz';
  var KEY = {
    wid: 'wally_wid', subscribed: 'wally_subscribed', known: 'wally_known',
    dismissed: 'wally_flyout_dismissed', teaserDismissed: 'wally_teaser_dismissed',
    views: 'wally_views'
  };
  var VIEWS_MAX = 20;
  var VIEWS_DAYS = 30;
  var SEEN = 'wally_flyout_seen'; // sessionStorage: it opened by itself once this visit
  // Klaviyo's live rules for its flyout (RL5rUs, read 26/09/2026): after 7 seconds AND 30%
  // scrolled, away for 5 days once closed, never for people who signed up or it recognises.
  var FLYOUT_DELAY_MS = 7000;
  var FLYOUT_SCROLL = 0.3;
  var DISMISS_DAYS = 5;
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function sget(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function sset(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }

  // Klaviyo's cookie holds only an anonymous id until Klaviyo knows who the person is
  // (clicked one of its emails, filled a form, checked out); then it carries their identity.
  function klaviyoKnows() {
    try {
      var m = document.cookie.match(/(?:^|; )__kla_id=([^;]+)/);
      if (!m) return false;
      var d = JSON.parse(atob(decodeURIComponent(m[1])));
      return !!(d.$exchange_id || d.$email);
    } catch (e) { return false; }
  }

  function post(path, body) {
    return fetch(API + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok || j.ok === false) throw new Error(j.error || 'status ' + r.status);
        return j;
      });
    });
  }

  // ---- 1. who is this? ------------------------------------------------------------
  // Links in our emails carry _wid. Keep it, and treat the person as already subscribed
  // so the flyout never offers them a signup they already have.
  var params = new URLSearchParams(window.location.search);
  var cfg = window.__wallyEmail || {};
  var wid = params.get('_wid');
  if (wid) { set(KEY.wid, wid); set(KEY.subscribed, '1'); }
  wid = wid || get(KEY.wid);
  // Known customers never get the signup offer: logged in (cfg.known), arrived from one of
  // Klaviyo's emails (_kx), or recognised by Klaviyo's cookie. Remembered once seen.
  if (cfg.known || params.get('_kx') || klaviyoKnows()) set(KEY.known, '1');

  // ---- 3. product views -----------------------------------------------------------
  // Views from before we knew them: [{ product_id, product_name, url, at }], newest last.
  function savedViews() {
    var oldest = Date.now() - VIEWS_DAYS * 864e5;
    try {
      return (JSON.parse(get(KEY.views) || '[]') || []).filter(function (v) { return v && Date.parse(v.at) > oldest; });
    } catch (e) { return []; }
  }

  function saveView() {
    var id = String(cfg.productId);
    var list = savedViews().filter(function (v) { return v.product_id !== id; });
    list.push({ product_id: id, product_name: cfg.productTitle || '', url: window.location.origin + window.location.pathname, at: new Date().toISOString() });
    set(KEY.views, JSON.stringify(list.slice(-VIEWS_MAX)));
  }

  function sendSavedViews() {
    var list = savedViews();
    if (!wid || !list.length) return;
    post('/t', { wid: wid, type: 'viewed_products', items: list })
      .then(function () { try { localStorage.removeItem(KEY.views); } catch (e) { /* private mode */ } })
      .catch(function () { /* try again on the next page */ });
  }

  function trackView() {
    if (!cfg.productId) return;
    if (!wid) { saveView(); return; }
    post('/t', {
      wid: wid,
      type: 'viewed_product',
      product_id: String(cfg.productId),
      product_name: cfg.productTitle || '',
      url: window.location.origin + window.location.pathname
    }).catch(function () { /* never bother the shopper */ });
  }
  sendSavedViews();
  trackView();

  // A form reply carries the site id for subscribers only; keep it, send what they looked
  // at before, and count this view too.
  function remember(j) {
    if (!j || !j.wid || j.wid === wid) return;
    wid = j.wid;
    set(KEY.wid, wid);
    sendSavedViews();
    trackView();
  }

  // ---- 2. notify-me requests --------------------------------------------------------
  function box(el) { return el.closest('.wally-preorder-notify'); }

  function say(b, msg, ok) {
    var m = b.querySelector('.wally-preorder-notify__msg');
    m.textContent = msg;
    m.hidden = false;
    m.classList.toggle('is-error', !ok);
  }

  function requestStock(b) {
    var input = b.querySelector('.wally-preorder-notify__email');
    var email = (input.value || '').trim();
    if (!EMAIL.test(email)) { say(b, 'Enter a valid email address.', false); input.focus(); return; }
    var d = b.dataset;
    var btn = b.querySelector('.wally-preorder-notify__submit');
    var hp = b.querySelector('.wally-hp');
    var optIn = b.querySelector('.wally-preorder-notify__subscribe'); // back in stock only
    btn.disabled = true;
    post('/forms/stock-request', {
      email: email,
      kind: d.kind === 'back_in_stock' ? 'back_in_stock' : 'preorder',
      variant_id: d.variantId,
      product_id: d.productId,
      product_name: d.productTitle,
      variant_name: d.variantTitle,
      product_url: d.productUrl,
      eta: d.eta || undefined,
      subscribe: !!(optIn && optIn.checked),
      website: hp ? hp.value : ''
    }).then(function (j) {
      remember(j);
      set(KEY.known, '1');
      if (optIn && optIn.checked) set(KEY.subscribed, '1');
      var t = document.querySelector('.wally-teaser');
      if (t) t.hidden = true;
      b.querySelector('.wally-preorder-notify__form').hidden = true;
      b.querySelector('.wally-preorder-notify__toggle').hidden = true;
      say(b, d.kind === 'back_in_stock'
        ? 'We’ve got you covered. As soon as it’s restocked, you’ll be the first to know.'
        : 'Done. We’ll email you as soon as it lands.', true);
    }).catch(function () {
      btn.disabled = false;
      say(b, 'Something went wrong. Please try again.', false);
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('.wally-preorder-notify__toggle');
    if (t) {
      var b = box(t);
      b.querySelector('.wally-preorder-notify__form').hidden = false;
      t.setAttribute('aria-expanded', 'true');
      b.querySelector('.wally-preorder-notify__email').focus();
      return;
    }
    var s = e.target.closest && e.target.closest('.wally-preorder-notify__submit');
    if (s) requestStock(box(s));
  });

  // Enter in the email field must not reach the surrounding add-to-cart form (Horizon's
  // product-form-component would submit it), so catch it in the capture phase.
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.keyCode === 13) && e.target.classList && e.target.classList.contains('wally-preorder-notify__email')) {
      e.preventDefault();
      e.stopPropagation();
      requestStock(box(e.target));
    }
  }, true);

  // ---- 1. signup flyout + teaser ----------------------------------------------------
  // The teaser ("10% Off, Easy", bottom left) is Klaviyo's: it appears once the flyout has
  // been closed or ignored, and reopens it. It has its own close, also good for 5 days.
  var fly = document.querySelector('.wally-flyout');
  var teaser = document.querySelector('.wally-teaser');
  if (!fly) return;

  var known = get(KEY.subscribed) || get(KEY.known);

  function within(key) {
    var at = Number(get(key) || 0);
    return at && Date.now() - at < DISMISS_DAYS * 864e5;
  }

  function showTeaser(on) {
    if (teaser) teaser.hidden = !on || !!known || !!cfg.noFlyout || !!within(KEY.teaserDismissed);
  }

  function open(auto) {
    fly.hidden = false;
    requestAnimationFrame(function () { fly.classList.add('is-open'); });
    showTeaser(false);
    if (auto) sset(SEEN, '1'); // once a visit: ignored, it becomes the teaser on the next page
  }

  function close(remember) {
    fly.classList.remove('is-open');
    setTimeout(function () { fly.hidden = true; }, 300);
    if (remember) { set(KEY.dismissed, String(Date.now())); showTeaser(true); }
  }

  if (!known && !cfg.noFlyout) {
    if (within(KEY.dismissed) || sget(SEEN)) {
      showTeaser(true);
    } else {
      var waited = false, scrolled = false;
      var maybeOpen = function () { if (waited && scrolled && fly.hidden) open(true); };
      var onScroll = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0 || window.scrollY / max >= FLYOUT_SCROLL) {
          scrolled = true;
          window.removeEventListener('scroll', onScroll);
          maybeOpen();
        }
      };
      setTimeout(function () { waited = true; maybeOpen(); }, FLYOUT_DELAY_MS);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  if (teaser) {
    teaser.querySelector('.wally-teaser__open').addEventListener('click', function () { open(false); });
    teaser.querySelector('.wally-teaser__close').addEventListener('click', function () {
      set(KEY.teaserDismissed, String(Date.now()));
      teaser.hidden = true;
    });
  }

  fly.querySelector('.wally-flyout__close').addEventListener('click', function () { close(true); });
  fly.querySelector('.wally-flyout__no').addEventListener('click', function () { close(true); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !fly.hidden) close(true); });

  var form = fly.querySelector('.wally-flyout__form');
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var input = form.querySelector('input[type="email"]');
    var msg = form.querySelector('.wally-flyout__msg');
    var btn = form.querySelector('.wally-flyout__submit');
    var email = (input.value || '').trim();
    if (!EMAIL.test(email)) {
      msg.textContent = 'Enter a valid email address.';
      msg.hidden = false;
      input.focus();
      return;
    }
    btn.disabled = true;
    post('/forms/subscribe', { email: email, form: 'flyout', website: form.querySelector('.wally-hp').value })
      .then(function (j) {
        remember(j);
        set(KEY.subscribed, '1');
        known = '1';
        showTeaser(false);
        var code = fly.querySelector('.wally-flyout__code');
        if (j.code) { code.textContent = j.code; code.hidden = false; }
        else {
          // No code for someone already on the list, a till sign-up, or anyone who had one before.
          fly.querySelector('.wally-flyout__withcode').hidden = true;
          fly.querySelector('.wally-flyout__nocode').hidden = false;
        }
        var img = fly.querySelector('.wally-flyout__image img');
        if (img && img.dataset.doneSrc) img.src = img.dataset.doneSrc; // Klaviyo's success step has its own photo
        form.hidden = true;
        fly.querySelector('.wally-flyout__done').hidden = false;
        // With a code on screen it stays open until closed (as Klaviyo's does), so it can be copied.
        if (!j.code) setTimeout(function () { close(false); }, 6000);
      })
      .catch(function () {
        btn.disabled = false;
        msg.textContent = 'Something went wrong. Please try again.';
        msg.hidden = false;
      });
  });
})();
